import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getAuditPending,
  approveAuditSelection,
  fetchFileBlobByKey,
  type AuditPendingItem,
  type AuditPendingResponse,
} from '../services/api';
import { FileText, Check, Tag, UserX, Loader2, AlertCircle, Fingerprint } from 'lucide-react';

export default function AuditReview() {
  const [data, setData] = useState<AuditPendingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewItem, setPreviewItem] = useState<AuditPendingItem | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [reCategorizeId, setReCategorizeId] = useState<string | null>(null);
  const [excludeId, setExcludeId] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const loadPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAuditPending();
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load audit pending');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  useEffect(() => {
    if (!previewItem?.fileUrl) {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
      return;
    }
    setPreviewLoading(true);
    fetchFileBlobByKey(previewItem.fileUrl)
      .then((blob) => {
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        const url = URL.createObjectURL(blob);
        previewUrlRef.current = url;
        setPreviewUrl(url);
      })
      .catch(() => setPreviewUrl(null))
      .finally(() => setPreviewLoading(false));
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
    };
  }, [previewItem?.id, previewItem?.fileUrl]);

  const handleApproveSelection = async () => {
    if (selectedIds.size === 0) return;
    setApproving(true);
    try {
      await approveAuditSelection(Array.from(selectedIds));
      setSelectedIds(new Set());
      await loadPending();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed');
    } finally {
      setApproving(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (items: AuditPendingItem[]) => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((i) => i.id)));
  };

  const allItems = data ? [...data.pending, ...data.fingerprinted] : [];
  const isTable = allItems.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Forensic Audit & Review</h1>
        {data && (data.pending.length > 0 || data.fingerprinted.length > 0) && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleApproveSelection}
              disabled={selectedIds.size === 0 || approving}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Approve selection ({selectedIds.size})
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          <AlertCircle className="h-5 w-5 shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : !data ? null : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            {data.pending.length === 0 && data.fingerprinted.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center text-gray-600 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
                <FileText className="mx-auto mb-2 h-12 w-12" />
                <p>No pending items. All business expenses meet confidence threshold.</p>
              </div>
            ) : (
              <>
                {data.pending.length > 0 && (
                  <section>
                    <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Pending review ({data.pending.length})
                    </h2>
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                      {isTable ? (
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead>
                            <tr>
                              <th className="px-3 py-2 text-left">
                                <input
                                  type="checkbox"
                                  checked={data.pending.length > 0 && selectedIds.size === data.pending.length}
                                  onChange={() => toggleSelectAll(data.pending)}
                                  className="rounded border-gray-300"
                                />
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Date</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Category</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Description</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {data.pending.map((row) => (
                              <AuditRow
                                key={row.id}
                                row={row}
                                selected={selectedIds.has(row.id)}
                                onToggleSelect={() => toggleSelect(row.id)}
                                onSelectPreview={() => setPreviewItem(row)}
                                onReCategorize={() => setReCategorizeId(row.id)}
                                onExclude={() => setExcludeId(row.id)}
                              />
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                          {data.pending.map((row) => (
                            <AuditCard
                              key={row.id}
                              row={row}
                              selected={selectedIds.has(row.id)}
                              onToggleSelect={() => toggleSelect(row.id)}
                              onSelectPreview={() => setPreviewItem(row)}
                              onReCategorize={() => setReCategorizeId(row.id)}
                              onExclude={() => setExcludeId(row.id)}
                            />
                          ))}
                        </ul>
                      )}
                    </div>
                  </section>
                )}
                {data.fingerprinted.length > 0 && (
                  <section>
                    <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      <Fingerprint className="h-4 w-4" /> Fingerprinted – same card, new vendor ({data.fingerprinted.length})
                    </h2>
                    <div className="overflow-hidden rounded-xl border border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-900/10">
                      <ul className="divide-y divide-amber-200 dark:divide-amber-800">
                        {data.fingerprinted.map((row) => (
                          <AuditCard
                            key={row.id}
                            row={row}
                            selected={selectedIds.has(row.id)}
                            onToggleSelect={() => toggleSelect(row.id)}
                            onSelectPreview={() => setPreviewItem(row)}
                            onReCategorize={() => setReCategorizeId(row.id)}
                            onExclude={() => setExcludeId(row.id)}
                            badge="Fingerprinted"
                          />
                        ))}
                      </ul>
                    </div>
                  </section>
                )}
              </>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <h2 className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 dark:border-gray-700 dark:text-gray-300">
              Document preview
            </h2>
            <div className="min-h-[360px] p-4">
              {!previewItem && (
                <p className="flex h-64 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                  Click a row to show the file
                </p>
              )}
              {previewLoading && (
                <div className="flex h-64 items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              )}
              {previewItem && previewUrl && !previewLoading && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {previewItem.filename} · {previewItem.date}
                  </p>
                  {previewItem.fileType?.includes('pdf') ? (
                    <iframe
                      src={previewUrl}
                      title={previewItem.filename}
                      className="h-[420px] w-full rounded border border-gray-200 dark:border-gray-600"
                    />
                  ) : (
                    <img
                      src={previewUrl}
                      alt={previewItem.filename}
                      className="max-h-[420px] w-full rounded border border-gray-200 object-contain dark:border-gray-600"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {(reCategorizeId || excludeId) && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          Re-Categorize and Exclude/Personal actions require backend endpoints (e.g. PATCH category, PATCH mode to family). Not yet implemented.
        </p>
      )}
    </div>
  );
}

function AuditRow({
  row,
  selected,
  onToggleSelect,
  onSelectPreview,
  onReCategorize,
  onExclude,
}: {
  row: AuditPendingItem;
  selected: boolean;
  onToggleSelect: () => void;
  onSelectPreview: () => void;
  onReCategorize: () => void;
  onExclude: () => void;
}) {
  return (
    <tr
      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
      onClick={onSelectPreview}
    >
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={selected} onChange={onToggleSelect} className="rounded border-gray-300" />
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{row.date}</td>
      <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">{row.category}</td>
      <td className="max-w-[200px] truncate px-3 py-2 text-sm text-gray-600 dark:text-gray-400" title={row.description}>
        {row.description}
      </td>
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); onReCategorize(); }}
            className="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Re-Categorize
          </button>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); onExclude(); }}
            className="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Exclude
          </button>
        </div>
      </td>
    </tr>
  );
}

function AuditCard({
  row,
  selected,
  onToggleSelect,
  onSelectPreview,
  onReCategorize,
  onExclude,
  badge,
}: {
  row: AuditPendingItem;
  selected: boolean;
  onToggleSelect: () => void;
  onSelectPreview: () => void;
  onReCategorize: () => void;
  onExclude: () => void;
  badge?: string;
}) {
  return (
    <li
      className="flex cursor-pointer items-start gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/30"
      onClick={onSelectPreview}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggleSelect}
        onClick={(e) => e.stopPropagation()}
        className="mt-1 rounded border-gray-300"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-gray-100">{row.date}</span>
          <span className="text-sm text-gray-500 dark:text-gray-400">{row.category}</span>
          {badge && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              {badge}
            </span>
          )}
        </div>
        <p className="mt-0.5 truncate text-sm text-gray-600 dark:text-gray-400">{row.description}</p>
        <div className="mt-2 flex gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={onReCategorize}
            className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200"
          >
            <Tag className="h-3 w-3" /> Re-Categorize
          </button>
          <button
            type="button"
            onClick={onExclude}
            className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200"
          >
            <UserX className="h-3 w-3" /> Exclude
          </button>
        </div>
      </div>
    </li>
  );
}
